import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaShieldAlt,
  FaCloudUploadAlt,
  FaCloud,
  FaChalkboardTeacher,
  FaGlobe,
} from 'react-icons/fa';

// Import des types
import { Service } from '../../types/services';

// Images locales depuis src/assets/images
import cybersecurityImg from '../../assets/images/security2.png';
import backupImg from '../../assets/images/d.jpg';
import cloudImg from '../../assets/images/cloud.jpg';
import trainingImg from '../../assets/images/database.jpg';
import worldSimImg from '../../assets/images/datacenter.jpg';

const serviceImages = {
  cybersecurity: cybersecurityImg,
  backup: backupImg,
  cloud: cloudImg,
  training: trainingImg,
  worldsim: worldSimImg,
};

const ServicesSection: React.FC = () => {
  const { t } = useTranslation('services');
  const location = useLocation();
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState<string>('cybersecurity');

  // Mémorisation des services avec useMemo
  const services: Service[] = useMemo(() => [
    {
      id: 'cybersecurity',
      title: t('services.cybersecurity.title'),
      icon: <FaShieldAlt className="text-white text-4xl" />,
      bgColor: 'bg-gradient-to-r from-[#4f837d] to-[#2e79b9]',
      features: [
        t('services.cybersecurity.features.protection'),
        t('services.cybersecurity.features.surveillance'),
        t('services.cybersecurity.features.tests'),
      ],
      details: {
        title: t('services.cybersecurity.details.title'),
        description: t('services.cybersecurity.details.description'),
        slides: [
          {
            title: t('services.cybersecurity.details.slides.vulnerability.title'),
            text: t('services.cybersecurity.details.slides.vulnerability.text'),
          },
          {
            title: t('services.cybersecurity.details.slides.ransomware.title'),
            text: t('services.cybersecurity.details.slides.ransomware.text'),
          },
          {
            title: t('services.cybersecurity.details.slides.surveillance.title'),
            text: t('services.cybersecurity.details.slides.surveillance.text'),
          },
        ],
      },
      image: serviceImages.cybersecurity,
    },
    {
      id: 'backup',
      title: t('services.backup.title'),
      icon: <FaCloudUploadAlt className="text-white text-4xl" />,
      bgColor: 'bg-gradient-to-r from-[#4f837d] to-[#2e79b9]',
      features: [
        t('services.backup.features.automated'),
        t('services.backup.features.recovery'),
        t('services.backup.features.storage'),
      ],
      details: {
        title: t('services.backup.details.title'),
        description: t('services.backup.details.description'),
        slides: [
          {
            title: t('services.backup.details.slides.automated.title'),
            text: t('services.backup.details.slides.automated.text'),
          },
          {
            title: t('services.backup.details.slides.recovery.title'),
            text: t('services.backup.details.slides.recovery.text'),
          },
          {
            title: t('services.backup.details.slides.storage.title'),
            text: t('services.backup.details.slides.storage.text'),
          },
        ],
      },
      image: serviceImages.backup,
    },
    {
      id: 'cloud',
      title: t('services.cloud.title'),
      icon: <FaCloud className="text-white text-4xl" />,
      bgColor: 'bg-gradient-to-r from-[#4f837d] to-[#2e79b9]',
      features: [
        t('services.cloud.features.infrastructure'),
        t('services.cloud.features.migration'),
        t('services.cloud.features.costs'),
      ],
      details: {
        title: t('services.cloud.details.title'),
        description: t('services.cloud.details.description'),
        slides: [
          {
            title: t('services.cloud.details.slides.scalable.title'),
            text: t('services.cloud.details.slides.scalable.text'),
          },
          {
            title: t('services.cloud.details.slides.migration.title'),
            text: t('services.cloud.details.slides.migration.text'),
          },
          {
            title: t('services.cloud.details.slides.costs.title'),
            text: t('services.cloud.details.slides.costs.text'),
          },
        ],
      },
      image: serviceImages.cloud,
    },
    {
      id: 'training',
      title: t('services.training.title'),
      icon: <FaChalkboardTeacher className="text-white text-4xl" />,
      bgColor: 'bg-gradient-to-r from-[#4f837d] to-[#2e79b9]',
      features: [
        t('services.training.features.courses'),
        t('services.training.features.workshops'),
        t('services.training.features.certifications'),
      ],
      details: {
        title: t('services.training.details.title'),
        description: t('services.training.details.description'),
        slides: [
          {
            title: t('services.training.details.slides.courses.title'),
            text: t('services.training.details.slides.courses.text'),
          },
          {
            title: t('services.training.details.slides.workshops.title'),
            text: t('services.training.details.slides.workshops.text'),
          },
          {
            title: t('services.training.details.slides.certifications.title'),
            text: t('services.training.details.slides.certifications.text'),
          },
        ],
      },
      image: serviceImages.training,
    },
    {
      id: 'worldsim',
      title: t('services.worldsim.title'),
      icon: <FaGlobe className="text-white text-4xl" />,
      bgColor: 'bg-gradient-to-r from-[#4f837d] to-[#2e79b9]',
      features: [
        t('services.worldsim.features.global_connectivity'),
        t('services.worldsim.features.roaming'),
        t('services.worldsim.features.cost_effective'),
      ],
      details: {
        title: t('services.worldsim.details.title'),
        description: t('services.worldsim.details.description'),
        slides: [
          {
            title: t('services.worldsim.details.slides.coverage.title'),
            text: t('services.worldsim.details.slides.coverage.text'),
          },
          {
            title: t('services.worldsim.details.slides.flexibility.title'),
            text: t('services.worldsim.details.slides.flexibility.text'),
          },
          {
            title: t('services.worldsim.details.slides.management.title'),
            text: t('services.worldsim.details.slides.management.text'),
          },
        ],
      },
      image: serviceImages.worldsim,
    },
  ], [t]);

  // Détecter le paramètre 'service' dans l'URL au chargement
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceParam = params.get('service');
    
    if (serviceParam && services.some(service => service.id === serviceParam)) {
      setActiveService(serviceParam);
    }
  }, [location, services]);

  // Fonction pour gérer le clic sur un service avec useCallback
  const handleServiceClick = useCallback((serviceId: string) => {
    setActiveService(serviceId);
    navigate(`?service=${serviceId}`, { replace: true });
    
    // Déclencher le défilement après un court délai
    setTimeout(() => {
      const element = document.getElementById('services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [navigate]);

  const selectedService = services.find((service) => service.id === activeService);

  return (
    <section
      id="services"
      className="relative py-12 bg-gradient-to-t from-white via-blue-50 to-white overflow-hidden"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Décorations subtiles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="w-4 h-4 bg-[#4f837d]/30 rounded-full absolute top-16 left-1/5 animate-float" />
        <div className="w-3 h-3 bg-[#2e79b9]/30 rounded-full absolute bottom-24 right-1/4 animate-float delay-700" />
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* Titre principal */}
        <h2 
          className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#155679]  animate-fade-in"
          itemProp="name"
        >
          {t('services.title')}
        </h2>

        {/* Layout principal */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Liste des services (à gauche) */}
          <div className="lg:w-1/3" role="tablist">
            <h4 className="text-xl font-semibold text-[#4f837d] mb-4">{t('services.our_services')}</h4>
            {services.map((service) => (
              <button
                key={service.id}
                role="tab"
                aria-selected={activeService === service.id}
                onClick={() => handleServiceClick(service.id)}
                className={`w-full flex items-center p-4 mb-2 rounded-lg bg-white/70 backdrop-blur-sm hover:bg-[#4f837d]/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2e79b9] ${
                  activeService === service.id ? 'bg-[#2e79b9]/20 shadow-lg' : ''
                }`}
                itemProp="hasOfferCatalog"
                itemScope
                itemType="https://schema.org/OfferCatalog"
              >
                <meta itemProp="name" content={service.title} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${service.bgColor} mr-4 shadow-md`}>
                  {service.icon}
                </div>
                <span className="text-[#2e79b9] font-medium" itemProp="description">
                  {service.title}
                </span>
              </button>
            ))}
          </div>

          {/* Section principale (à droite) */}
          <div className="lg:w-2/3">
            {/* En-tête avec image et overlay */}
            <div className="bg-white rounded-3xl shadow-2xl mb-6 overflow-hidden">
              <div className="relative">
                {/* Image avec overlay */}
                <div className="relative h-64 lg:h-80">
                  <img
                    src={selectedService?.image}
                    alt={t('services.alt_image', { title: selectedService?.title })}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="800"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4f837d]/90 to-[#2e79b9]/90"></div>
                  
                  {/* Contenu superposé */}
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    <div className="text-center text-white max-w-2xl">
                      <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                        {selectedService?.icon}
                      </div>
                      <h3 
                        className="text-4xl font-bold mb-4"
                        itemProp="name"
                      >
                        {selectedService?.details.title}
                      </h3>
                      <p 
                        className="text-xl text-white/90 leading-relaxed"
                        itemProp="description"
                      >
                        {selectedService?.details.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Grille des détails */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedService?.details.slides.map((slide, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-[#4f837d]"
                >
                  <h4 
                    className="font-semibold text-lg mb-3 text-[#4f837d]"
                    itemProp="name"
                  >
                    {slide.title}
                  </h4>
                  <p 
                    className="text-gray-600 text-sm leading-relaxed"
                    itemProp="description"
                  >
                    {slide.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translateY(0); 
          }
          50% { 
            transform: translateY(-10px); 
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        @media (max-width: 1024px) {
          .lg\\:w-1\\/3,
          .lg\\:w-2\\/3 {
            width: 100%;
          }
          .md\\:grid-cols-2,
          .lg\\:grid-cols-3 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;